<template>
	<Listbox v-model="selected" as="div">
		<ListboxLabel class="text-sm font-medium text-gray-700 flex align-center">
			{{ label }}
			<span v-if="tooltipMessage" class="pl-3 inline-flex">
				<help-tool-tip :tooltip-message="tooltipMessage" />
			</span>
		</ListboxLabel>
		<div class="mt-1 relative flex">
			<ListboxButton
				:disabled="isDisabled"
				class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-secondary sm:text-sm"
				:class="{
					'disabled:bg-gray-100': isDisabled,
					'bg-yellow-150': required,
					'border-red-400': errorMessage,
				}"
			>
				<span v-if="selected" class="block truncate">{{ selected.name }}</span>
				<span
					v-else
					class="text-gray-400 cursor-default font-normal block truncate select-none relative"
				>
					{{ placeholder || `${label} auswählen` }}
				</span>
				<div
					v-if="errorMessage"
					class="absolute inset-y-0 right-4 pr-3 flex items-center pointer-events-none"
				>
					<ExclamationCircleIcon
						class="h-5 w-5 text-red-400"
						aria-hidden="true"
					/>
				</div>
				<span
					class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
				>
					<SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
				</span>
			</ListboxButton>

			<transition
				leave-active-class="transition ease-in duration-100"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<ListboxOptions
					class="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-20"
				>
					<ListboxOption
						v-for="option in options"
						:key="option.id"
						v-slot="{ active, selected }"
						as="template"
						:value="option"
					>
						<li
							:class="[
								active ? 'text-white bg-secondary' : 'text-gray-900',
								'cursor-default select-none relative py-2 pl-3 pr-9',
							]"
						>
							<span
								:class="[
									selected ? 'font-semibold' : 'font-normal',
									'block truncate',
								]"
							>
								{{ option.name }}
							</span>

							<span
								v-if="selected"
								:class="[
									active ? 'text-white' : 'text-secondary',
									'absolute inset-y-0 right-0 flex items-center pr-4',
								]"
							>
								<CheckIcon class="h-5 w-5" aria-hidden="true" />
							</span>
						</li>
					</ListboxOption>
				</ListboxOptions>
			</transition>
		</div>
		<p v-show="errorMessage" class="text-red-500 text-xs mt-1">
			{{ errorMessage }}
		</p>
	</Listbox>
</template>

<script>
import HelpToolTip from '@/components/Common/HelpToolTip.vue'
import { useModelWrapper } from '@/utils/modelWrapper'
import {
	Listbox,
	ListboxButton,
	ListboxLabel,
	ListboxOption,
	ListboxOptions,
} from '@headlessui/vue'
import {
	CheckIcon,
	SelectorIcon,
	ExclamationCircleIcon,
} from '@heroicons/vue/solid'
import { computed } from 'vue'

export default {
	components: {
		Listbox,
		ListboxButton,
		ListboxLabel,
		ListboxOption,
		ListboxOptions,
		CheckIcon,
		SelectorIcon,
		HelpToolTip,
		ExclamationCircleIcon,
	},
	props: {
		label: {
			type: String,
			required: true,
		},
		options: {
			type: Array,
			required: true,
		},
		placeholder: {
			type: String,
			required: false,
			default: '',
		},
		tooltipMessage: {
			type: String,
			required: false,
			default: '',
		},
		modelValue: {
			type: [String, Number, Object],
			required: false,
			default: null,
		},
		errorMessage: {
			type: String,
			required: false,
			default: '',
		},
		required: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	setup(props, { emit }) {
		const isDisabled = computed(() => {
			if (props.options && props.options.length > 0) return false
			return true
		})

		return {
			selected: useModelWrapper(props, emit, 'modelValue'),
			isDisabled,
		}
	},
}
</script>

<style lang="scss" scoped></style>
